"use client";
import { useEffect, useState } from "react";

const TITLE_OPTIONS = ["Dr", "Prof.", "Mr", "Ms", "Mrs"];
const IMAGE_BASE_URL = "http://techzenondev.com/event/storage/app/public/";

export default function ProfileManagementPage() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);

  // Add Admin modal state
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [addForm, setAddForm] = useState({
    name: "",
    email: "",
    password: "",
    title: "",
    country_of_residence: "",
    phone_number: "",
    whatsapp_number: "",
    image_file: null,
  });
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addSuccessMsg, setAddSuccessMsg] = useState("");
  const [showAddPassword, setShowAddPassword] = useState(false);

  // GET: fetch profile
  useEffect(() => {
    const fetchAdminUser = async () => {
      try {
        const token =
          localStorage.getItem("adminToken") ||
          localStorage.getItem("token");

        if (!token) {
          setError("Auth token missing in localStorage");
          setLoading(false);
          return;
        }

        const res = await fetch(
          "https://eventscons.com/eventbackendnew/api/adminusers",
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!res.ok) {
          const text = await res.text();
          console.error("API error body:", text);
          throw new Error(
            `Request failed with status ${res.status}`
          );
        }

        const json = await res.json();
        if (json && json.user) {
          const u = json.user;

          setForm({
            id: u.id,
            name: u.name || "",
            email: u.email || "",
            email_verified_at: u.email_verified_at || "",
            role: u.role || "",
            created_at: u.created_at || "",
            updated_at: u.updated_at || "",
            image: u.image || "",
            biography: u.biography || "",
            registration_source_field: u.registration_source_field || "",
            phone_number: u.phone_number || "",
            whatsapp_number: u.whatsapp_number || "",
            specialization: u.specialization || "",
            nationality: u.nationality || "",
            country_of_residence: u.country_of_residence || "",
            status: u.status || "",
            isRejected: u.isRejected ?? "",
            company_name: u.company_name || "",
            contact_person: u.contact_person || "",
            company_address: u.company_address || "",
            conference_id: u.conference_id || "",
            certificatePDF: u.certificatePDF || "",
            isFromAbstract: u.isFromAbstract ?? "",
            is_oral_presentation: u.is_oral_presentation ?? "",
            is_poster: u.is_poster ?? "",
            title: u.title || "",
            passwordh: u.passwordh || "",
            image_file: null,
          });
        } else {
          setError("Invalid response format from server");
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchAdminUser();
  }, []);

  // GET: countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await fetch(
          "https://eventscons.com/eventbackendnew/api/countries"
        );
        const json = await res.json();
        const data = Array.isArray(json.data) ? json.data : [];
        setCountries(data);
      } catch (err) {
        console.error("Countries fetch error:", err);
        setCountries([]);
      } finally {
        setCountriesLoading(false);
      }
    };
    fetchCountries();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (file) => {
    setForm((prev) => ({ ...prev, image_file: file }));
  };

  const getSelectedCountry = () => {
    if (!form?.country_of_residence) return null;
    return countries.find(
      (c) => c.name === form.country_of_residence
    );
  };

  const getImageUrl = (image) => {
    if (!image) return "";
    if (image.startsWith("http")) return image;
    return IMAGE_BASE_URL + image;
  };

  // UPDATE: profile
  const handleUpdate = async () => {
    if (!form) return;
    setSaving(true);
    setSuccessMsg("");
    setError(null);

    try {
      const token =
        localStorage.getItem("adminToken") ||
        localStorage.getItem("token");

      if (!token) {
        setError("Auth token missing in localStorage");
        setSaving(false);
        return;
      }

      const formData = new FormData();

      Object.entries(form).forEach(([key, value]) => {
        if (key === "image_file") return;
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });

      if (form.image_file instanceof File) {
        formData.append("image", form.image_file);
      }

      const res = await fetch(
        `https://eventscons.com/eventbackendnew/api/users/${form.id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      let text = "";
      try {
        text = await res.text();
        console.log("Update raw response:", res.status, text);
      } catch (e) {
        console.warn("Could not read response body:", e);
      }

      setSuccessMsg("Profile updated successfully.");
    } catch (err) {
      console.error("Update error:", err);
    } finally {
      setSaving(false);
    }
  };

  // Add Admin handlers
  const handleAddChange = (field, value) => {
    setAddForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddImageChange = (file) => {
    setAddForm((prev) => ({ ...prev, image_file: file }));
  };

  const getAddSelectedCountry = () => {
    if (!addForm.country_of_residence) return null;
    return countries.find(
      (c) => c.name === addForm.country_of_residence
    );
  };

  const handleAddAdmin = async () => {
    setAddSaving(true);
    setAddError(null);
    setAddSuccessMsg("");

    // frontend password validation
    if (!addForm.password || addForm.password.length < 8) {
      setAddError("Password must be at least 8 characters.");
      setAddSaving(false);
      return;
    }

    try {
      const token =
        localStorage.getItem("adminToken") ||
        localStorage.getItem("token");

      if (!token) {
        setAddError("Auth token missing in localStorage");
        setAddSaving(false);
        return;
      }

      const formData = new FormData();
      formData.append("name", addForm.name || "");
      formData.append("email", addForm.email || "");
      formData.append("password", addForm.password || "");
      formData.append("title", addForm.title || "");
      formData.append(
        "country_of_residence",
        addForm.country_of_residence || ""
      );
      formData.append("phone_number", addForm.phone_number || "");
      formData.append("whatsapp_number", addForm.whatsapp_number || "");
      formData.append("isAdmin", true);

      if (addForm.image_file instanceof File) {
        formData.append("image", addForm.image_file);
      }

      const res = await fetch(
        "https://eventscons.com/eventbackendnew/api/users",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      let raw = "";
      try {
        raw = await res.text();
        console.log("Add admin raw response:", res.status, raw);
      } catch (e) {
        console.warn("Could not read add admin response body:", e);
      }

      if (!res.ok) {
        let msg = "Failed to create admin.";
        try {
          const json = raw ? JSON.parse(raw) : null;
          // Example: 500 {"message":"Failed to create user.","error":"The email has already been taken. (and 1 more error)"}
          if (json?.error) {
            msg = json.error;
          } else if (json?.message) {
            msg = json.message;
          } else if (json?.errors) {
            const firstKey = Object.keys(json.errors)[0];
            if (firstKey && json.errors[firstKey]?.[0]) {
              msg = json.errors[firstKey][0];
            }
          }
        } catch {
          if (raw) msg = raw;
        }
        setAddError(msg);
        return;
      }

      setAddSuccessMsg("Admin created successfully.");
      setAddForm({
        name: "",
        email: "",
        password: "",
        title: "",
        country_of_residence: "",
        phone_number: "",
        whatsapp_number: "",
        image_file: null,
      });
    } catch (err) {
      console.error("Add admin error:", err);
      setAddError(err.message || "Failed to create admin");
    } finally {
      setAddSaving(false);
    }
  };

  return (
    <main
      style={{
        minHeight: "100vh",
        padding: "2rem",
        backgroundColor: "#f5f5f5",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "820px",
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          padding: "24px 28px",
        }}
      >
        {form && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              marginBottom: "1.5rem",
            }}
          >
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: "999px",
                overflow: "hidden",
                border: "2px solid #e5e7eb",
                backgroundColor: "#f3f4f6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.75rem",
                color: "#9ca3af",
              }}
            >
              {form.image || form.image_file ? (
                <img
                  src={
                    form.image_file
                      ? URL.createObjectURL(form.image_file)
                      : getImageUrl(form.image)
                  }
                  alt="Profile"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                "No Image"
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <span
                style={{
                  fontSize: "1rem",
                  fontWeight: 600,
                  color: "#111827",
                }}
              >
                {form.name || "Profile"}
              </span>
              <span
                style={{
                  fontSize: "0.85rem",
                  color: "#6b7280",
                  marginBottom: "0.5rem",
                }}
              >
                {form.email}
              </span>
              <label
                style={{
                  display: "inline-block",
                  padding: "0.35rem 0.8rem",
                  borderRadius: "999px",
                  border: "1px solid #9B1321",
                  color: "#9B1321",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  cursor: "pointer",
                  width: "fit-content",
                }}
              >
                Edit Image
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageChange(file);
                  }}
                />
              </label>
            </div>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          <h1
            style={{
              fontSize: "1.8rem",
              fontWeight: 600,
              marginBottom: "1.0rem",
              color: "#111827",
              borderBottom: "1px solid #e5e7eb",
              paddingBottom: "0.75rem",
              flexGrow: 1,
            }}
          >
            Profile Management
          </h1>

          <button
            type="button"
            onClick={() => {
              setShowAddAdmin(true);
              setAddError(null);
              setAddSuccessMsg("");
            }}
            style={{
              padding: "0.55rem 1rem",
              borderRadius: "999px",
              border: "none",
              backgroundColor: "#2563eb",
              color: "#ffffff",
              fontSize: "0.9rem",
              fontWeight: 500,
              cursor: "pointer",
              boxShadow: "0 8px 20px rgba(37,99,235,0.35)",
              marginBottom: "0.5rem",
            }}
          >
            Add Admin
          </button>
        </div>

        {loading && (
          <p style={{ color: "#6b7280", fontSize: "0.95rem" }}>Loading...</p>
        )}

        {error && (
          <p style={{ color: "#b91c1c", fontSize: "0.95rem", marginBottom: 12 }}>
            Error: {error}
          </p>
        )}

        {successMsg && (
          <p
            style={{
              color: "#15803d",
              fontSize: "0.95rem",
              marginBottom: 12,
            }}
          >
            {successMsg}
          </p>
        )}

        {!loading && !error && form && (
          <>
            <section
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                padding: "16px 18px",
                background:
                  "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
                marginBottom: "1.25rem",
              }}
            >
              <div
                style={{
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: "#6b7280",
                  marginBottom: "0.75rem",
                }}
              >
                Admin ID: #{form.id}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(240px, 1fr))",
                  gap: "1rem 1.25rem",
                }}
              >
                <Field
                  label="Name"
                  value={form.name}
                  onChange={(v) => handleChange("name", v)}
                />

                <Field
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={(v) => handleChange("email", v)}
                />

                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      color: "#4b5563",
                      marginBottom: "0.35rem",
                    }}
                  >
                    Title
                  </label>
                  <select
                    value={form.title || ""}
                    onChange={(e) => handleChange("title", e.target.value)}
                    style={{
                      padding: "0.55rem 0.7rem",
                      borderRadius: "8px",
                      border: "1px solid #d1d5db",
                      backgroundColor: "#f9fafb",
                      fontSize: "0.9rem",
                      color: "#111827",
                      outline: "none",
                    }}
                  >
                    <option value="">Select title</option>
                    {TITLE_OPTIONS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <Field
                  label="Password"
                  type="password"
                  value={form.passwordh}
                  onChange={(v) => handleChange("passwordh", v)}
                />

                <Field
                  label="Phone Number"
                  value={form.phone_number}
                  onChange={(v) => handleChange("phone_number", v)}
                />

                <Field
                  label="Whatsapp Number"
                  value={form.whatsapp_number}
                  onChange={(v) => handleChange("whatsapp_number", v)}
                />

                <div style={{ position: "relative" }}>
                  <label
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      color: "#4b5563",
                      marginBottom: "0.35rem",
                      display: "block",
                    }}
                  >
                    Country of Residence
                  </label>
                  <div
                    style={{
                      padding: "0.55rem 0.7rem",
                      borderRadius: "8px",
                      border: "1px solid #d1d5db",
                      backgroundColor: "#f9fafb",
                      fontSize: "0.9rem",
                      color: "#111827",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      !countriesLoading &&
                      setCountryDropdownOpen((prev) => !prev)
                    }
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      {countriesLoading ? (
                        <span
                          style={{ color: "#6b7280", fontSize: "0.85rem" }}
                        >
                          Loading...
                        </span>
                      ) : (
                        <>
                          {getSelectedCountry()?.flag_url && (
                            <img
                              src={getSelectedCountry().flag_url}
                              alt={getSelectedCountry().name}
                              style={{
                                width: 20,
                                height: 14,
                                objectFit: "cover",
                                borderRadius: 3,
                              }}
                            />
                          )}
                          <span>
                            {form.country_of_residence || "Select country"}
                          </span>
                        </>
                      )}
                    </div>
                    <span
                      style={{
                        fontSize: "0.8rem",
                        color: "#6b7280",
                      }}
                    >
                      ▼
                    </span>
                  </div>

                  {countryDropdownOpen && !countriesLoading && (
                    <div
                      style={{
                        position: "absolute",
                        zIndex: 20,
                        backgroundColor: "#ffffff",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        marginTop: "4px",
                        maxHeight: 220,
                        overflowY: "auto",
                        width: "100%",
                        boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                      }}
                    >
                      {countries.map((c) => (
                        <div
                          key={c.id}
                          style={{
                            padding: "0.45rem 0.7rem",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            cursor: "pointer",
                            fontSize: "0.9rem",
                            color: "#111827",
                            backgroundColor:
                              c.name === form.country_of_residence
                                ? "#f3f4f6"
                                : "#ffffff",
                          }}
                          onClick={() => {
                            handleChange("country_of_residence", c.name);
                            setCountryDropdownOpen(false);
                          }}
                        >
                          {c.flag_url && (
                            <img
                              src={c.flag_url}
                              alt={c.name}
                              style={{
                                width: 20,
                                height: 14,
                                objectFit: "cover",
                                borderRadius: 3,
                              }}
                            />
                          )}
                          <span>{c.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </section>

            <button
              type="button"
              onClick={handleUpdate}
              disabled={saving}
              style={{
                marginTop: "0.5rem",
                padding: "0.6rem 1.4rem",
                borderRadius: "999px",
                border: "none",
                backgroundColor: saving ? "#9ca3af" : "#9B1321",
                color: "#ffffff",
                fontSize: "0.95rem",
                fontWeight: 500,
                cursor: saving ? "not-allowed" : "pointer",
                boxShadow: "0 8px 20px rgba(37,99,235,0.35)",
                transition: "background-color 0.15s ease",
              }}
            >
              {saving ? "Saving..." : "Update Profile"}
            </button>
          </>
        )}

        {/* Add Admin Modal */}
        {showAddAdmin && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 50,
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: 520,
                backgroundColor: "#ffffff",
                borderRadius: 12,
                boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
                padding: "20px 22px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <h2
                  style={{
                    fontSize: "1.2rem",
                    fontWeight: 600,
                    color: "#111827",
                  }}
                >
                  Add Admin
                </h2>
                <button
                  type="button"
                  onClick={() => setShowAddAdmin(false)}
                  style={{
                    border: "none",
                    background: "transparent",
                    fontSize: "1.2rem",
                    cursor: "pointer",
                    color: "#6b7280",
                  }}
                >
                  ✕
                </button>
              </div>

              {addError && (
                <p
                  style={{
                    color: "#b91c1c",
                    fontSize: "0.9rem",
                    marginBottom: 8,
                  }}
                >
                  {addError}
                </p>
              )}
              {addSuccessMsg && (
                <p
                  style={{
                    color: "#15803d",
                    fontSize: "0.9rem",
                    marginBottom: 8,
                  }}
                >
                  {addSuccessMsg}
                </p>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "0.9rem 1rem",
                  marginBottom: 12,
                }}
              >
                <Field
                  label="Name"
                  value={addForm.name}
                  onChange={(v) => handleAddChange("name", v)}
                />
                <Field
                  label="Email"
                  type="email"
                  value={addForm.email}
                  onChange={(v) => handleAddChange("email", v)}
                />

                {/* Password with eye icon + hint */}
                <div
                  style={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <label
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      color: "#4b5563",
                      marginBottom: "0.35rem",
                    }}
                  >
                    Password
                  </label>
                  <input
                    type={showAddPassword ? "text" : "password"}
                    value={addForm.password}
                    onChange={(e) =>
                      handleAddChange("password", e.target.value)
                    }
                    style={{
                      padding: "0.55rem 2.2rem 0.55rem 0.7rem",
                      borderRadius: "8px",
                      border: "1px solid #d1d5db",
                      backgroundColor: "#f9fafb",
                      fontSize: "0.9rem",
                      color: "#111827",
                      outline: "none",
                    }}
                  />
                  <span
                    onClick={() => setShowAddPassword((p) => !p)}
                    style={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(4px)",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                      color: "#6b7280",
                      userSelect: "none",
                    }}
                  >
                    {showAddPassword ? "🙈" : "👁"}
                  </span>
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: "#6b7280",
                      marginTop: "0.25rem",
                    }}
                  >
                    Minimum 8 characters.
                  </span>
                </div>

                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      color: "#4b5563",
                      marginBottom: "0.35rem",
                    }}
                  >
                    Title
                  </label>
                  <select
                    value={addForm.title}
                    onChange={(e) =>
                      handleAddChange("title", e.target.value)
                    }
                    style={{
                      padding: "0.55rem 0.7rem",
                      borderRadius: "8px",
                      border: "1px solid #d1d5db",
                      backgroundColor: "#f9fafb",
                      fontSize: "0.9rem",
                      color: "#111827",
                      outline: "none",
                    }}
                  >
                    <option value="">Select title</option>
                    {TITLE_OPTIONS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <Field
                  label="Phone Number"
                  value={addForm.phone_number}
                  onChange={(v) => handleAddChange("phone_number", v)}
                />
                <Field
                  label="Whatsapp Number"
                  value={addForm.whatsapp_number}
                  onChange={(v) =>
                    handleAddChange("whatsapp_number", v)
                  }
                />

                {/* Country list */}
                <div style={{ position: "relative" }}>
                  <label
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      color: "#4b5563",
                      marginBottom: "0.35rem",
                      display: "block",
                    }}
                  >
                    Country of Residence
                  </label>
                  <div
                    style={{
                      padding: "0.55rem 0.7rem",
                      borderRadius: "8px",
                      border: "1px solid #d1d5db",
                      backgroundColor: "#f9fafb",
                      fontSize: "0.9rem",
                      color: "#111827",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {countriesLoading ? (
                      <span
                        style={{ color: "#6b7280", fontSize: "0.85rem" }}
                      >
                        Loading...
                      </span>
                    ) : (
                      <>
                        {getAddSelectedCountry()?.flag_url && (
                          <img
                            src={getAddSelectedCountry().flag_url}
                            alt={getAddSelectedCountry().name}
                            style={{
                              width: 20,
                              height: 14,
                              objectFit: "cover",
                              borderRadius: 3,
                            }}
                          />
                        )}
                        <span>
                          {addForm.country_of_residence || "Select country"}
                        </span>
                      </>
                    )}
                  </div>

                  {!countriesLoading && (
                    <div
                      style={{
                        marginTop: 4,
                        maxHeight: 160,
                        overflowY: "auto",
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        backgroundColor: "#ffffff",
                      }}
                    >
                      {countries.map((c) => (
                        <div
                          key={c.id}
                          style={{
                            padding: "0.45rem 0.7rem",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            cursor: "pointer",
                            fontSize: "0.9rem",
                            color: "#111827",
                            backgroundColor:
                              c.name === addForm.country_of_residence
                                ? "#f3f4f6"
                                : "#ffffff",
                          }}
                          onClick={() =>
                            handleAddChange("country_of_residence", c.name)
                          }
                        >
                          {c.flag_url && (
                            <img
                              src={c.flag_url}
                              alt={c.name}
                              style={{
                                width: 20,
                                height: 14,
                                objectFit: "cover",
                                borderRadius: 3,
                              }}
                            />
                          )}
                          <span>{c.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Image upload */}
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      color: "#4b5563",
                      marginBottom: "0.35rem",
                    }}
                  >
                    Profile Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleAddImageChange(file);
                    }}
                    style={{
                      fontSize: "0.85rem",
                    }}
                  />
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "0.75rem",
                  marginTop: 8,
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowAddAdmin(false)}
                  style={{
                    padding: "0.5rem 1.1rem",
                    borderRadius: "999px",
                    border: "1px solid #d1d5db",
                    backgroundColor: "#ffffff",
                    color: "#374151",
                    fontSize: "0.9rem",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddAdmin}
                  disabled={addSaving}
                  style={{
                    padding: "0.5rem 1.3rem",
                    borderRadius: "999px",
                    border: "none",
                    backgroundColor: addSaving ? "#9ca3af" : "#2563eb",
                    color: "#ffffff",
                    fontSize: "0.9rem",
                    fontWeight: 500,
                    cursor: addSaving ? "not-allowed" : "pointer",
                    boxShadow: "0 8px 20px rgba(37,99,235,0.35)",
                  }}
                >
                  {addSaving ? "Saving..." : "Create Admin"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  textarea = false,
  readOnly = false,
}) {
  const baseStyle = {
    padding: "0.55rem 0.7rem",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    backgroundColor: readOnly ? "#f3f4f6" : "#f9fafb",
    fontSize: "0.9rem",
    color: "#111827",
    outline: "none",
    resize: "vertical",
    minHeight: textarea ? "80px" : undefined,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <label
        style={{
          fontSize: "0.8rem",
          fontWeight: 500,
          color: "#4b5563",
          marginBottom: "0.35rem",
        }}
      >
        {label}
      </label>
      {textarea ? (
        <textarea
          value={value || ""}
          onChange={(e) => !readOnly && onChange(e.target.value)}
          readOnly={readOnly}
          style={baseStyle}
        />
      ) : (
        <input
          type={type}
          value={value || ""}
          onChange={(e) => !readOnly && onChange(e.target.value)}
          readOnly={readOnly}
          style={baseStyle}
        />
      )}
    </div>
  );
}